import type { AssetKindT } from './assets';

import {
  GB_BYTES,
  KB_BYTES,
  MAX_AUDIO_SIZE,
  MAX_DOCUMENT_SIZE,
  MAX_IMAGE_SIZE,
  MAX_VIDEO_SIZE,
  MB_BYTES,
} from './constants';

/**
 * Formats the given number of bytes into a human-friendly string.
 */
export function formatFileSize(bytes: number): string {
  if (bytes > GB_BYTES) return `${Math.round(bytes / GB_BYTES)}GB`;
  if (bytes > MB_BYTES) return `${Math.round(bytes / MB_BYTES)}MB`;
  if (bytes > KB_BYTES) return `${Math.round(bytes / KB_BYTES)}KB`;
  return `${bytes} bytes`;
}

type IsAllowedT = {
  reason?: string;
  value: boolean;
};

// eslint-disable-next-line complexity
export function isAllowedFile(
  file: File,
  accept: AssetKindT[],
): IsAllowedT {
  const ext = file.name.match(/(\.\w+)$/)?.[1]?.toLowerCase() ?? '';

  const isAllowedSize = (
    limit: number,
    fileKind: AssetKindT,
  ): IsAllowedT => {
    const value = file.size < limit;
    return {
      reason: value ? undefined : `Your "${file.name}" ${fileKind} size (${formatFileSize(file.size)}) is larger than the allowed limit ${formatFileSize(limit)}.`,
      value,
    };
  };

  switch (ext) {
    case '.avi':
    case '.mov':
    case '.mp4':
      if (accept.includes('VIDEO')) {
        return isAllowedSize(MAX_VIDEO_SIZE, 'VIDEO');
      }
      break;

    case '.doc':
    case '.docx':
    case '.pdf':
      if (accept.includes('DOCUMENT')) {
        return isAllowedSize(MAX_DOCUMENT_SIZE, 'DOCUMENT');
      }
      break;

    case '.heic':
    case '.jpeg':
    case '.jpg':
    case '.png':
      if (accept.includes('IMAGE')) {
        return isAllowedSize(MAX_IMAGE_SIZE, 'IMAGE');
      }
      break;

    case '.m4a':
    case '.mp3':
    case '.wav':
      if (accept.includes('AUDIO')) {
        return isAllowedSize(MAX_AUDIO_SIZE, 'AUDIO');
      }
      break;

    default: break;
  }

  return {
    reason: `Your "${file.name}" file type (${ext}) is not recognized as an allowed ${accept.join(', ')}`,
    value: false,
  };
}
